/**
 * Modern Bold · <mb-discount-popup> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * 15% signup-offer dialog rendered by sections/modern-discount-popup.liquid.
 * Owns display frequency only — code issuance is enforced server-side by
 * the "Welcome new subscriber" automation (fires once per email, ever).
 *
 * Decision chain on connect (first match wins):
 *   design mode → open/close via editor select events, zero storage I/O
 *   pending flag → the popup's own form just posted: reopen in the
 *     success or error state Liquid rendered, record signed-up on success
 *   customer_posted in URL, no pending → the FOOTER newsletter posted;
 *     same welcome email goes out, so record signed-up silently
 *   signed-up flag → never show again
 *   dismissed < 24h ago → stay hidden
 *   otherwise → wait for Customer Privacy consent (marketing AND
 *     analytics allowed), then arm the configured trigger
 *     (immediate | delay | scroll)
 *
 * DOM contract (rendered by the section):
 *   [data-mb-popup-panel]           dialog card, tabindex="-1" focus target
 *   [data-mb-popup-close]           overlay, X button, success CTA
 *   [data-mb-popup-email]           email input
 *   [data-mb-popup-submit]          submit button (data-loading-label attr)
 *   [data-mb-popup-btn-label]       label span swapped while submitting
 *   [data-mb-popup-spinner]         spinner span, hidden until submit
 *   [data-mb-popup-success]         success branch marker (post-submit render)
 *   [data-mb-popup-success-heading] tabindex="-1" focus target on success
 *   [data-mb-popup-error]           role="alert" error marker
 *   data-trigger / data-delay-seconds / data-scroll-percent / data-section-id
 */

if (!customElements.get('mb-discount-popup')) {
  const DISMISS_KEY = 'mb-discount-popup-dismissed-at';
  const SIGNED_KEY  = 'mb-discount-popup-signed-up';
  const PENDING_KEY = 'mb-discount-popup-pending';
  // Shared with <mb-promo-countdown>: whichever popup opens stamps the time,
  // so the other can stay quiet instead of interrupting on top of it.
  const POPUP_AT_KEY = 'mb-popup-last-shown-at';
  const DISMISS_TTL = 24 * 60 * 60 * 1000; // 1 day
  const CLOSE_TRANSITION_MS = 220;
  const BUSY_RETRY_MS = 5000;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Storage throws in some privacy modes — never let that break the page.
  // Failed reads degrade to "show the popup"; failed writes just mean the
  // visitor may see it again on the next page.
  const readDismissedAt = () => {
    try { return parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10) || 0; }
    catch { return 0; }
  };
  const recordDismissal = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };
  const isDismissed = () => {
    const at = readDismissedAt();
    return at > 0 && Date.now() - at < DISMISS_TTL;
  };
  const isSignedUp = () => {
    try { return localStorage.getItem(SIGNED_KEY) === '1'; } catch { return false; }
  };
  const recordSignedUp = () => {
    try { localStorage.setItem(SIGNED_KEY, '1'); } catch { /* ignore */ }
  };
  const markPending = () => {
    try { sessionStorage.setItem(PENDING_KEY, '1'); } catch { /* ignore */ }
  };
  const stampShown = () => {
    try { sessionStorage.setItem(POPUP_AT_KEY, String(Date.now())); } catch { /* ignore */ }
  };
  // Read-and-clear: the flag only means anything on the single load right
  // after the popup's form posted.
  // A live offer popup (sections/modern-offer-popup.liquid) takes this
  // popup's place until its end time passes.
  const offerRunning = () => {
    const endsAt = Date.parse(document.querySelector('mb-offer-popup')?.dataset.endsAt || '');
    return Number.isFinite(endsAt) && Date.now() < endsAt;
  };
  const consumePending = () => {
    try {
      const pending = sessionStorage.getItem(PENDING_KEY) === '1';
      sessionStorage.removeItem(PENDING_KEY);
      return pending;
    } catch { return false; }
  };

  class MBDiscountPopup extends HTMLElement {
    #cleanups = [];
    #refs = {};
    #state = 'form';
    #lastActive = null;
    #retryId = null;
    #hideTimerId = null;
    #idleLabel = '';

    connectedCallback() {
      this.#cacheRefs();
      this.#bindEvents();

      // Theme editor: the merchant drives visibility by selecting the
      // section — never auto-trigger, never touch the merchant's storage.
      if (window.Shopify?.designMode) {
        this.#bindDesignMode();
        return;
      }

      if (consumePending()) {
        if (this.#refs.success) {
          recordSignedUp();
          this.#cleanUrl();
          this.#open('success');
          return;
        }
        if (this.#refs.error) {
          this.#open('error');
          return;
        }
        // Stale flag from an aborted POST (network failure, back/stop
        // mid-navigation) — fall through to the normal chain. Except on
        // Shopify's captcha interstitial, which renders this section too:
        // opening over the challenge the visitor is solving would block it.
        if (window.location.pathname === '/challenge') return;
      }

      // form.errors is shared by every customer form on the page: when the
      // FOOTER newsletter errors, our Liquid renders that error (and the
      // visitor's email) inside this popup too. Without our pending flag
      // it wasn't our submit — don't arm and surface someone else's error.
      if (this.#refs.error) return;

      // customer_posted without our pending flag = the footer newsletter
      // posted. Same subscriber, same welcome email — suppress forever.
      if (new URLSearchParams(window.location.search).get('customer_posted') === 'true') {
        recordSignedUp();
        this.#cleanUrl();
        return;
      }

      if (isSignedUp() || isDismissed() || offerRunning()) return;

      this.#armTriggerWhenConsented();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // ── DOM cache ──────────────────────────────────────────────────────────

    #cacheRefs() {
      this.#refs = {
        panel:          this.querySelector('[data-mb-popup-panel]'),
        email:          this.querySelector('[data-mb-popup-email]'),
        success:        this.querySelector('[data-mb-popup-success]'),
        successHeading: this.querySelector('[data-mb-popup-success-heading]'),
        error:          this.querySelector('[data-mb-popup-error]'),
        form:           this.querySelector('form'),
        submitBtn:      this.querySelector('[data-mb-popup-submit]'),
        submitLabel:    this.querySelector('[data-mb-popup-btn-label]'),
        spinner:        this.querySelector('[data-mb-popup-spinner]'),
      };
      this.#idleLabel = this.#refs.submitLabel?.textContent ?? '';
    }

    // ── Event wiring ───────────────────────────────────────────────────────

    #bindEvents() {
      // One delegate covers the overlay, the X, and the success CTA.
      const onClick = (e) => {
        if (e.target.closest('[data-mb-popup-close]')) this.#close();
      };
      this.addEventListener('click', onClick);
      this.#cleanups.push(() => this.removeEventListener('click', onClick));

      const onDocKeydown = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.#close();
      };
      document.addEventListener('keydown', onDocKeydown);
      this.#cleanups.push(() => document.removeEventListener('keydown', onDocKeydown));

      const onTrap = (e) => this.#trapFocus(e);
      this.addEventListener('keydown', onTrap);
      this.#cleanups.push(() => this.removeEventListener('keydown', onTrap));

      this.#cleanups.push(() => {
        clearTimeout(this.#retryId);
        clearTimeout(this.#hideTimerId);
      });

      // The customer form is a full-page POST: mark the pending flag (how
      // the next load knows OUR form posted, not the footer's) and flip the
      // button into its loading state so the wait is visible. aria-disabled
      // doubles as the double-submit guard.
      if (this.#refs.form) {
        const onSubmit = (e) => {
          if (this.#refs.submitBtn?.getAttribute('aria-disabled') === 'true') {
            e.preventDefault();
            return;
          }
          markPending();
          this.#setLoading();
          this.#trackClaim();
        };
        this.#refs.form.addEventListener('submit', onSubmit);
        this.#cleanups.push(() => this.#refs.form.removeEventListener('submit', onSubmit));

        // Back/forward-cache restore resurrects the page with the loading
        // UI still applied — reset so the form is usable again.
        const onPageShow = (e) => { if (e.persisted) this.#resetLoading(); };
        window.addEventListener('pageshow', onPageShow);
        this.#cleanups.push(() => window.removeEventListener('pageshow', onPageShow));
      }
    }

    #bindDesignMode() {
      const matches = (e) => e.detail?.sectionId === this.dataset.sectionId;
      const onSelect = (e) => {
        if (matches(e)) this.#open(this.#refs.success ? 'success' : 'form');
      };
      const onDeselect = (e) => {
        if (matches(e)) this.#close({ restoreFocus: false });
      };
      document.addEventListener('shopify:section:select', onSelect);
      document.addEventListener('shopify:section:deselect', onDeselect);
      this.#cleanups.push(() => {
        document.removeEventListener('shopify:section:select', onSelect);
        document.removeEventListener('shopify:section:deselect', onDeselect);
      });
    }

    // ── Consent gate (Shopify Customer Privacy API) ────────────────────────

    // The popup is a marketing surface and writes to localStorage, so it
    // only arms once the visitor's marketing AND analytics consent are
    // granted. On shops/regions where consent isn't required the API
    // returns true out of the box, so this is a no-op there. If the API
    // fails to load we stay closed — no consent signal, no popup.
    #armTriggerWhenConsented() {
      this.#withCustomerPrivacy((privacy) => {
        if (this.#consentGranted(privacy)) {
          this.#armTrigger();
          return;
        }
        // No consent yet — Shopify fires this when the visitor answers
        // the cookie banner. Re-check then; a "no" keeps us waiting (a
        // later banner re-open can still flip it to yes).
        const onConsent = () => {
          if (!this.#consentGranted(privacy)) return;
          document.removeEventListener('visitorConsentCollected', onConsent);
          this.#armTrigger();
        };
        document.addEventListener('visitorConsentCollected', onConsent);
        this.#cleanups.push(() => document.removeEventListener('visitorConsentCollected', onConsent));
      });
    }

    #consentGranted(privacy) {
      try {
        return privacy.marketingAllowed() && privacy.analyticsProcessingAllowed();
      } catch {
        return false;
      }
    }

    #withCustomerPrivacy(callback) {
      if (window.Shopify?.customerPrivacy) {
        callback(window.Shopify.customerPrivacy);
        return;
      }
      // Not loaded yet — pull in Shopify's consent-tracking feature bundle.
      if (typeof window.Shopify?.loadFeatures !== 'function') return;
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error || !window.Shopify.customerPrivacy) return;
          callback(window.Shopify.customerPrivacy);
        }
      );
    }

    // ── URL cleanup ────────────────────────────────────────────────────────

    // Shopify's customer form redirect appends ?customer_posted=true#<form-id>.
    // Once consumed it's just noise in the address bar — and a reload with it
    // still present would re-run the post-submit paths. Strip it in place.
    #cleanUrl() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('customer_posted') !== 'true') return;
      url.searchParams.delete('customer_posted');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // ── Analytics ──────────────────────────────────────────────────────────

    // GA4 event on claim: fires only after HTML validation passed and the
    // double-submit guard cleared, so counts = real submit attempts. GA4's
    // beacon transport survives the full-page POST navigation. Consent is
    // already granted here — the popup only opens post-consent. Falls back
    // to a bare dataLayer push if only GTM is present; never throws.
    #trackClaim() {
      try {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'discount_popup_claim', { method: 'discount_popup' });
        } else if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({ event: 'discount_popup_claim', method: 'discount_popup' });
        }
      } catch { /* analytics must never break the submit */ }
    }

    // ── Submit loading state ───────────────────────────────────────────────

    // aria-disabled instead of disabled: the button keeps focus so AT users
    // hear the state change (CLAUDE.md §2.6); the submit guard above blocks
    // the actual double-fire.
    #setLoading() {
      const { submitBtn, submitLabel, spinner } = this.#refs;
      if (!submitBtn) return;
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.classList.add('is-loading');
      if (spinner) spinner.hidden = false;
      if (submitLabel) submitLabel.textContent = submitBtn.dataset.loadingLabel || 'Sending…';
    }

    #resetLoading() {
      const { submitBtn, submitLabel, spinner } = this.#refs;
      if (!submitBtn) return;
      submitBtn.removeAttribute('aria-disabled');
      submitBtn.classList.remove('is-loading');
      if (spinner) spinner.hidden = true;
      if (submitLabel && this.#idleLabel) submitLabel.textContent = this.#idleLabel;
    }

    // ── Trigger arming ─────────────────────────────────────────────────────

    #armTrigger() {
      const trigger = this.dataset.trigger || 'delay';

      if (trigger === 'scroll') {
        const threshold = parseInt(this.dataset.scrollPercent || '30', 10);
        const onScroll = () => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          // Unscrollable page: the threshold can never be crossed — treat
          // it as met so the popup isn't unreachable on short pages.
          const progress = scrollable <= 0 ? 100 : (window.scrollY / scrollable) * 100;
          if (progress < threshold) return;
          window.removeEventListener('scroll', onScroll);
          this.#open('form');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        this.#cleanups.push(() => window.removeEventListener('scroll', onScroll));
        // Initial check after hydration settles — covers short pages and
        // deep links that land mid-page without a scroll event.
        const initialId = setTimeout(onScroll, 400);
        this.#cleanups.push(() => clearTimeout(initialId));
        return;
      }

      // 'immediate' still waits one beat so opening doesn't race initial
      // layout and focus; 'delay' honors the merchant-set seconds.
      const ms = trigger === 'immediate'
        ? 400
        : parseInt(this.dataset.delaySeconds || '5', 10) * 1000;
      const id = setTimeout(() => this.#open('form'), ms);
      this.#cleanups.push(() => clearTimeout(id));
    }

    // ── Focus trap ─────────────────────────────────────────────────────────

    #trapFocus(e) {
      if (e.key !== 'Tab' || !this.classList.contains('is-open')) return;
      // tabIndex >= 0 keeps the pointer-only overlay (tabindex="-1") and
      // the hidden tags input out of the list — counting the overlay as
      // `first` let Shift+Tab walk out of the dialog into the page behind.
      const focusable = [...this.querySelectorAll(FOCUSABLE)].filter(
        (el) => el.tabIndex >= 0 && !el.hasAttribute('hidden') && !el.closest('[hidden]')
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable.at(-1);
      // Focus legitimately sits on a tabindex="-1" target after open (the
      // panel, the success heading) — outside the list, so wrap explicitly
      // instead of letting the browser tab out of the aria-modal dialog.
      if (!focusable.includes(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    // ── Open / close ───────────────────────────────────────────────────────

    #open(state) {
      if (this.classList.contains('is-open')) return;

      // Defer while the visitor is engaged elsewhere: another modal has
      // the stage, or they're mid-entry in some field (stealing focus
      // while typing loses keystrokes). Cart drawer matches .active only —
      // Dawn never removes its 'animate' class after the first open.
      const typing = document.activeElement?.matches?.(
        'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), textarea, select, [contenteditable]:not([contenteditable="false"])'
      );
      const busy = typing
        || document.body.classList.contains('mb-search-open')
        || document.querySelector('cart-drawer.active')
        || document.querySelector('.mb-promo__modal.is-open');
      if (busy) {
        clearTimeout(this.#retryId);
        this.#retryId = setTimeout(() => this.#open(state), BUSY_RETRY_MS);
        return;
      }

      this.#state = state;
      this.dataset.state = state;
      this.#lastActive = document.activeElement;

      // A reopen within the close transition would otherwise let the stale
      // hide timer fire and leave the popup is-open but display:none.
      clearTimeout(this.#hideTimerId);
      this.hidden = false;
      // Reflow so the .is-open transition runs from the hidden→visible boundary.
      // eslint-disable-next-line no-unused-expressions
      this.offsetHeight;
      this.classList.add('is-open');
      document.body.classList.add('mb-popup-open');
      if (!window.Shopify?.designMode) stampShown();

      // Focus per state: the panel (not the input) in form state so SR
      // users hear the dialog label and offer before landing in a field;
      // the heading on success; the input when reopening with an error.
      requestAnimationFrame(() => {
        if (state === 'success' && this.#refs.successHeading) {
          this.#refs.successHeading.focus();
        } else if (state === 'error' && this.#refs.email) {
          this.#refs.email.focus();
        } else {
          this.#refs.panel?.focus();
        }
      });
    }

    #close({ restoreFocus = true } = {}) {
      if (!this.classList.contains('is-open')) return;

      // A success-state close isn't a dismissal — the signed-up flag
      // already suppresses the popup permanently. Design mode never
      // touches the merchant's storage.
      if (this.#state !== 'success' && !window.Shopify?.designMode) {
        recordDismissal();
      }

      this.classList.remove('is-open');
      document.body.classList.remove('mb-popup-open');

      // Wait for the CSS transition before removing from layout entirely.
      this.#hideTimerId = setTimeout(() => { this.hidden = true; }, CLOSE_TRANSITION_MS);

      if (!restoreFocus) return;
      // Never let focus drop to <body>: restore the pre-open element if it
      // still exists, else hand focus to #MainContent (tabindex="-1").
      const previous = this.#lastActive;
      if (previous && previous !== document.body && previous.isConnected
          && typeof previous.focus === 'function') {
        previous.focus();
      } else {
        document.getElementById('MainContent')?.focus();
      }
    }
  }

  customElements.define('mb-discount-popup', MBDiscountPopup);
}
